import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Redirect, Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { ComponentContext } from '../../context/componentContext';
import { AuthContext } from '../../context/authContext';

import UnexpectedError from '../../components/UnexpectedError';
import AuthHeader from '../../container/Layout/AuthHeader';
import AuthFooter from '../../container/Layout/AuthFooter';
import { Plain } from '../../components/Loading';
import FormInput from '../../components/form/input';
import FormButton from '../../components/form/button';

import 'react-toastify/dist/ReactToastify.css';

const PasswordResetSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('*Password is Required!')
    .min(8, '*Password should at least be 8 characters!'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], '*Passwords do not match')
    .required('*Password Confirm is Required!')
});

const PasswordReset = ({ location: { search } }) => {
  document.title = 'CrowdFarmer | Password Reset';

  const { reload, loading, setLoading, unexpectedError, setUnexpectedError } = useContext(
    ComponentContext
  );
  const { verifyToken, updatePassword, isAuthenticated } = useContext(AuthContext);

  let [redirect, setRedirect] = useState(false);
  let [userId, setUserId] = useState(null);
  let [error, setError] = useState(null);
  let [success, setSuccess] = useState(null);

  useEffect(() => {
    let token = search.split('?')[1];
    if (token) {
      (async () => {
        try {
          setLoading(true);
          const response = await verifyToken({ token, access: 'password-reset' });
          setLoading(false);
          if (response.code === 200) {
            setUserId(response.data);
          } else {
            setError(response.data.message);
          }
        } catch (error) {
          setUnexpectedError('Unexpected Network Error!');
        }
      })();
    } else {
      setRedirect(true);
    }
  }, [reload, search, setLoading, setUnexpectedError, verifyToken]);

  if (redirect) {
    return <Redirect to="/unauthorized" />;
  }

  if (isAuthenticated()) return <Redirect to={'/home'} />;

  return (
    <Fragment>
      <AuthHeader />
      {loading ? (
        <Plain />
      ) : (
        <>
          {unexpectedError ? (
            <UnexpectedError unexpectedError={unexpectedError} />
          ) : (
            <div className="mobile-bg mx-auto">
              <div className="mobile-bg-color">
                <div className="container-inner mx-auto">
                  <div className="flex items-center justify-center h-screen">
                    <div className="rounded shadow-lg bg-white text-center">
                      {!success && userId ? (
                        <div className="py-6 px-10 w-115 mt-6">
                          <h3 className="font-header text-xl">Reset your password</h3>

                          <Formik
                            initialValues={{
                              userId,
                              newPassword: '',
                              confirmPassword: ''
                            }}
                            validationSchema={PasswordResetSchema}
                            onSubmit={(values, { setSubmitting }) => {
                              (async () => {
                                try {
                                  setSubmitting(true);
                                  const response = await updatePassword(values);
                                  setSubmitting(false);
                                  if (response.code === 200) {
                                    setSuccess(response.message);
                                  } else {
                                    throw response.data.message;
                                  }
                                } catch (error) {
                                  let errMgs = error;
                                  if (typeof error !== 'string') {
                                    errMgs = 'Unexpected server error';
                                  }
                                  toast.error(errMgs, {
                                    position: toast.POSITION.TOP_RIGHT
                                  });
                                }
                              })();
                            }}
                          >
                            {({
                              values,
                              errors,
                              touched,
                              handleChange,
                              handleBlur,
                              handleSubmit,
                              isSubmitting
                            }) => (
                              <Fragment>
                                <form
                                  className="flex flex-wrap mt-10 text-left"
                                  onSubmit={handleSubmit}
                                >
                                  <FormInput
                                    error={errors.newPassword}
                                    touched={touched.newPassword}
                                    className="w-1/2 pr-2"
                                    change={handleChange}
                                    blur={handleBlur}
                                    inputValue={values.newPassword}
                                    type="password"
                                    id="newPassword"
                                    name="newPassword"
                                    placeholder="*********"
                                    title="New Password"
                                    inputType
                                    passwordShow
                                  />

                                  <FormInput
                                    error={errors.confirmPassword}
                                    touched={touched.confirmPassword}
                                    className="w-1/2 pl-2"
                                    change={handleChange}
                                    blur={handleBlur}
                                    inputValue={values.confirmPassword}
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    placeholder="*********"
                                    title="Confirm New Password"
                                    inputType
                                    passwordShow
                                  />

                                  <FormButton
                                    loading={isSubmitting}
                                    name="Reset"
                                    className="w-full"
                                    color="bg-cf-300"
                                    btnPad="px-4 py-3 my-3"
                                  />
                                </form>
                                <div className="text-left text-sm mt-2">
                                  <p className="font-bold">Note:</p>
                                  <ul className="text-xs">
                                    <li>* You cannot make use of your old password!</li>
                                    <li>* password length should be between 8-15 characters!</li>
                                  </ul>
                                </div>
                              </Fragment>
                            )}
                          </Formik>
                        </div>
                      ) : (
                        <>
                          <div className="py-6 px-10 w-115 mt-6">
                            <div className="text-center">
                              <h3 className="font-header text-xl capitalize">{success}</h3>
                            </div>
                          </div>
                          <Link to="/">
                            <button className="py-3 px-4 my-3 bg-cf-300 text-green-200 p-2 rounded shadow-cfw focus:outline-none">
                              Login
                            </button>
                          </Link>
                        </>
                      )}
                      {error && (
                        <>
                          <div className="py-6 px-10 w-115 mt-6">
                            <div className="text-center">
                              <h3 className="font-header text-xl">Token Verification Failed!</h3>
                            </div>
                            <small>{error}</small>
                          </div>
                          <Link to="/forget-password">
                            <button className="py-3 px-4 my-3 bg-cf-300 text-green-200 p-2 rounded shadow-cfw focus:outline-none">
                              Get Fresh Token
                            </button>
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <AuthFooter />
    </Fragment>
  );
};

export default PasswordReset;
